// Die Fahrt von der Seite (B24, Fabi 29.10.2026; Erste Fassung, Runden 2 und 7): Man fährt durch die drei
// Stimmungsbilder des Bioms, nach public/art/fahrt/durchfahrt.json und fahrt-spiel.json des Grafik-Leads (Hinweise dort).
// Der Weg w verschiebt die Ebenen, die Anlegestellen und den Durchlass; die Tageszeit t folgt aus dem Weg und bestimmt Ort,
// Kulisse, Himmel, Wetter und Licht. Dazu das Unwetter nach Sturmstufe (Sturmfassung der Ebenen, Wolkenbänke, dunkle
// Decke, Regen, Blätter, Wetterleuchten) und das Boot mit der Truppe darin (boat.ts). Reine Darstellung; public/art/
// gehört dem Grafik-Lead, hier wird nur gelesen.
// Runde 8 (Schlussrunde des Grafik-Leads, Fabis Notizen vom 20.10.2026): Seegang ab Nah (sea.ts), das Boot schaukelt mit
// allem, was darauf ist (rock.ts), und Eingeholt ist deutlich schlimmer als Dicht: tiefe Wolkendecke, tiefere Wolken,
// grauer Schleier über der Ferne, Dunst am Horizont, längerer und schrägerer Regen.
import { drawBoat, drawRocked, PAD, rockAt, shadeBoat, type Hit } from './boat.ts';
import type { Aboard } from './crew.ts';
import { img, loadDurchfahrt, loadFahrt, type Durchfahrt, type FahrtSpiel, type Ort, type Wetter } from './data.ts';
import { dayTime, ortMitte, ortWege } from './path.ts';
import { rockOf, swayAt, type Rock } from './rock.ts';
import { drawHaze, drawSea, hexRgb, hullBottom, seaHeight, waterColor } from './sea.ts';

export type { Hit };

export type SceneState = {
  biome: string;
  /** Weg seit dem Morgen in Bildpixeln (Ebene vorn) */
  w: number;
  /** Strecken des Tages (Anlegestellen + 1) */
  n: number;
  /** Wetter der Sturmstufe, weich übergeblendet */
  wetter: Wetter;
  /** Uhrzeit für Wellen, Schaukeln, Regen, Bewegungen */
  now: number;
  /** Phase des Schaukelns (Hinweis „Schaukeln des Boots“); ohne liegt das Boot still */
  sway?: number;
  /** So weit sind die Kräusel beim Liegen weitergezogen (Bildpixel) */
  ripple: number;
  boat: string;
  view: 'quer' | 'aussen';
  crew: Aboard[];
  chat?: boolean;
  look?: boolean;
  /** 0 bis 1: Nacht zwischen zwei Tagen */
  night: number;
  /** Blitz passend zum Donner (0 bis 1); ohne Ton blitzt es nach dem Abstand der Sturmstufe */
  flash?: number;
};

const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
const smooth = (x: number) => {
  const t = clamp(x, 0, 1);
  return t * t * (3 - 2 * t);
};
function mixHex(a: string, b: string, w: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return '#' + pa.map((v, i) => Math.round(v + (pb[i]! - v) * w).toString(16).padStart(2, '0')).join('');
}
const rgba = (hex: string, a: number) => `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)}, ${a})`;

/**
 * Eine Ebene um versatz verschoben, gekachelt nur mit dem sauberen Teil [x0, x1] des Kerns (Hinweis „Sauber kacheln“):
 * Kachel k liegt bei versatz + x0 + k · (x1 − x0), gerade k richtig herum, ungerade gespiegelt
 */
function layer(ctx: CanvasRenderingContext2D, d: Durchfahrt, atlas: HTMLImageElement, part: [number, number, number, number], versatz: number, rows: [number, number], clean: [number, number]): void {
  const [px, py] = part;
  const [x0, x1] = clean;
  const span = x1 - x0;
  const [top, bottom] = rows;
  const h = bottom - top;
  if (h <= 0 || span <= 0) return;
  const k0 = Math.floor((-versatz - x0) / span) - 1;
  for (let k = k0; k <= k0 + Math.ceil(d.breite / span) + 2; k++) {
    const x = Math.round(versatz + x0 + k * span);
    if (x > d.breite || x + span < 0) continue;
    if (((k % 2) + 2) % 2 === 0) ctx.drawImage(atlas, px + d.rand + x0, py + top, span, h, x, top, span, h);
    else {
      ctx.save();
      ctx.translate(x + span, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(atlas, px + d.rand + x0, py + top, span, h, 0, top, span, h);
      ctx.restore();
    }
  }
}

/** Ein Bild mit dem Licht der Tageszeit multipliziert, zwischengespeichert */
const toned = new Map<string, HTMLCanvasElement>();
function tone(src: HTMLImageElement, color: string): HTMLCanvasElement {
  const key = `${src.src}|${color}`;
  let c = toned.get(key);
  if (c) return c;
  c = document.createElement('canvas');
  c.width = src.naturalWidth;
  c.height = src.naturalHeight;
  const x = c.getContext('2d')!;
  x.drawImage(src, 0, 0);
  x.globalCompositeOperation = 'multiply';
  x.fillStyle = color;
  x.fillRect(0, 0, c.width, c.height);
  x.globalCompositeOperation = 'destination-in';
  x.drawImage(src, 0, 0);
  if (toned.size > 60) toned.clear();
  toned.set(key, c);
  return c;
}

/** Schein einer Laterne und ihre Lichtbahn im Wasser */
function lamp(ctx: CanvasRenderingContext2D, x: number, y: number, strength: number, now: number, wl: number, seed = 0): void {
  if (strength <= 0) return;
  const flicker = 0.92 + 0.08 * Math.sin(now * 7.3 + seed * 1.7) * Math.sin(now * 3.1 + seed);
  const a = strength * flicker;
  const g = ctx.createRadialGradient(x, y, 0, x, y, 16);
  g.addColorStop(0, `rgba(255, 214, 140, ${0.5 * a})`);
  g.addColorStop(1, 'rgba(255, 214, 140, 0)');
  ctx.fillStyle = g;
  ctx.fillRect(x - 16, y - 16, 32, 32);
  // Lichtbahn: kurze helle Striche unter der Laterne auf dem Wasser
  if (y > wl - 4) return;
  for (let i = 0; i < 7; i++) {
    const yy = Math.round(wl + 3 + i * 5);
    const len = 4 - Math.floor(i / 2) + Math.round(Math.sin(now * 2 + i + seed) * 1.2);
    ctx.fillStyle = `rgba(255, 210, 130, ${0.28 * a * (1 - i / 8)})`;
    ctx.fillRect(Math.round(x - len / 2 + Math.sin(now * 1.6 + i * 1.3 + seed) * 1.5), yy, Math.max(1, len), 1);
  }
}

/**
 * Ein Schleier über dem Bild: in der Ferne (bei hor) voll, oben 45 %, ganz vorn 35 % (Hinweis „Wetter“, Morgendunst);
 * fern (Runde 8) ist bei hor + 6 voll und hat zusätzlich 90 % bei hor − 34, also 40 Pixel darüber
 */
function veil(ctx: CanvasRenderingContext2D, d: Durchfahrt, color: string, alpha: number, hor: number, clip?: [number, number], above?: number): void {
  if (alpha <= 0.002) return;
  const g = ctx.createLinearGradient(0, 0, 0, d.hoehe);
  const h = clamp(hor / d.hoehe, 0.05, 0.95);
  g.addColorStop(0, rgba(color, alpha * 0.45));
  if (above !== undefined) g.addColorStop(clamp((hor - 40) / d.hoehe, 0.01, h - 0.01), rgba(color, alpha * above));
  g.addColorStop(h, rgba(color, alpha));
  g.addColorStop(1, rgba(color, alpha * 0.35));
  ctx.fillStyle = g;
  const [x0, x1] = clip ?? [0, d.breite];
  ctx.fillRect(x0, 0, x1 - x0, d.hoehe);
}

const LEAF: Record<string, string[]> = {
  tannenwald: ['#b8732e', '#8f5a2a', '#c99a3e'],
  moor: ['#9a8a3a', '#7b6a34', '#b39a4a'],
  weinhaenge: ['#b5452f', '#d27a2c', '#8e3a2a'],
};

/** Ein Bild zur Zeit des Zustands auf die Leinwand (640 × 360); false, solange die Bilder noch laden */
export function drawFahrt(canvas: HTMLCanvasElement, s: SceneState, hits: Hit[] = []): boolean {
  const all = loadFahrt();
  if (!all) return false;
  const { d, spiel, boote } = all;
  const bio = d.biome[s.biome] ?? Object.values(d.biome)[0]!;
  if (canvas.width !== d.breite || canvas.height !== d.hoehe) {
    canvas.width = d.breite;
    canvas.height = d.hoehe;
  }
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  const T = d.takt;
  const orte = bio.orte;
  const span = T.halten + T.uebergang;
  const L = spiel.spiel.strecke.weg;
  const W = s.wetter;
  // Tageszeit aus dem Weg (Hinweis „Tageszeit“)
  const tau = clamp(dayTime(s.w, s.n, T, L, orte.length), 0, ortMitte(T, orte.length - 1));
  const wege = ortWege(s.n, L, orte.length);
  // Welcher Ort, welcher Übergang (Hinweis „Übergang“): Übergang i hat seine Mitte bei t_i, die Kulisse fährt dabei durchs Bild
  const kw = bio.kulisse.w;
  let passed = 0;
  let xk: number | null = null;
  let skyFromI = 0;
  let w = 0;
  for (let i = 0; i < orte.length - 1; i++) {
    const ti = T.halten / 2 + i * span + span / 2;
    const x = 320 - (tau - ti) * ((640 + kw) / T.kulisse);
    if (x < -kw / 2) passed = i + 1;
    else if (x <= 640 + kw / 2 && xk === null) xk = x;
    // Wetter: weiche Kurve; wird es trüber, ist es vor der Kulisse fertig, wird es klarer, erst danach
    const a = orte[i]!;
    const b = orte[i + 1]!;
    const lage = clamp((b.wetter.truebe - a.wetter.truebe) * 3, -1, 1);
    const ww = smooth((tau - (ti - (T.wetter * (1 + lage)) / 2)) / T.wetter);
    if (ww > 0) {
      skyFromI = i;
      w = ww;
    }
  }
  if (w >= 1) {
    skyFromI = Math.min(orte.length - 1, skyFromI + 1);
    w = 0;
  }
  const A = orte[passed]!;
  const B = orte[Math.min(orte.length - 1, passed + (xk !== null ? 1 : 0))]!;
  const skyFrom = orte[skyFromI]!;
  const skyTo = orte[Math.min(orte.length - 1, skyFromI + 1)]!;
  const blend = skyTo !== skyFrom ? w : 0;
  const atlasOf = (o: Ort) => {
    const a = d.atlas[o.id];
    return a ? { a, im: img(`fahrt/${a.datei}`) } : null;
  };
  const aA = atlasOf(A);
  const aB = atlasOf(B);
  if (!aA?.im || !aB?.im) return false;
  const tempo = (id: string) => d.ebenen.find((e) => e.id === id)?.tempo ?? 0;
  const sturmTempo = (id: string) => d.ebenen.find((e) => e.id === id)?.sturmTempo ?? 1;
  const hor = Math.round((A.hor ?? 250) + ((B.hor ?? 250) - (A.hor ?? 250)) * blend);
  const versatzOf = (o: Ort, id: string) => -(s.w - (wege[orte.indexOf(o)]?.mitte ?? 0)) * tempo(id);

  // Wo die Kulisse ist, wird links der alte und rechts der neue Ort gezeichnet
  const both = (draw: (o: Ort, at: NonNullable<ReturnType<typeof atlasOf>>, clip: [number, number]) => void) => {
    if (xk === null || A === B) {
      draw(A, aA, [0, d.breite]);
      return;
    }
    const cut = Math.round(xk);
    for (const [side, o, at] of [['left', A, aA], ['right', B, aB]] as const) {
      ctx.save();
      ctx.beginPath();
      const clip: [number, number] = side === 'left' ? [0, Math.max(0, cut)] : [cut, d.breite];
      ctx.rect(clip[0], 0, clip[1] - clip[0], d.hoehe);
      ctx.clip();
      draw(o, at, clip);
      ctx.restore();
    }
  };

  // Himmel: blendet mit dem Wetter über
  const sky = (o: Ort, alpha: number) => {
    const at = atlasOf(o);
    const p = at?.a.teile[`${o.id}_himmel`];
    if (!at?.im || !p) return;
    ctx.globalAlpha = alpha;
    ctx.drawImage(at.im, p[0], p[1], p[2], p[3], 0, 0, d.breite, d.hoehe);
    const ps = at.a.teile[`${o.id}_himmel_sturm`];
    if (ps && W.sturm > 0) {
      ctx.globalAlpha = alpha * clamp(W.sturm * sturmTempo('himmel'), 0, 1);
      ctx.drawImage(at.im, ps[0], ps[1], ps[2], ps[3], 0, 0, d.breite, d.hoehe);
    }
    ctx.globalAlpha = 1;
  };
  sky(skyFrom, 1);
  if (blend > 0) sky(skyTo, blend);

  // Wolkenbänke: welche man sieht und wie dunkel sie sind, sagt die Sturmstufe; sie ziehen mit dem Wind
  const clouds = (o: Ort, alpha: number) => {
    const at = atlasOf(o);
    if (!at?.im) return;
    for (let j = 1; j <= 4; j++) {
      const a = clamp(W.baenke[j - 1] ?? 0, 0, 1) * alpha;
      if (a <= 0.01) continue;
      const p = at.a.teile[`${o.id}_wolke${j}`];
      if (!p) continue;
      const off = -((s.now * W.wind * (5 + j * 3) + j * 173) % p[2]);
      // Runde 8: Eingeholt liegen die Wolken tiefer (tief)
      const y = o.wolkenY + Math.round(W.tief ?? 0);
      ctx.globalAlpha = a;
      for (const x of [off, off + p[2]]) ctx.drawImage(at.im, p[0], p[1], p[2], p[3], Math.round(x), y, p[2], p[3]);
      const dark = (W.baenkeDunkel[j - 1] ?? 0) * a;
      if (dark > 0.01) {
        const sh = bankShade(at.im, p);
        ctx.globalAlpha = dark;
        for (const x of [off, off + p[2]]) ctx.drawImage(sh, Math.round(x), y);
      }
    }
    // Runde 8: die tiefe, dunkle Wolkendecke (decke): Wolkenbank 4 noch einmal, dunkel, darunter
    const decke = W.decke ?? 0;
    const p4 = at.a.teile[`${o.id}_wolke4`];
    if (decke > 0.002 && p4) {
      const off = -((s.now * (4 + 9 * W.wind)) % p4[2]);
      const y = o.wolkenY + Math.round(1.6 * (W.tief ?? 0)) + 6;
      const sh = bankShade(at.im, p4);
      for (const x of [off, off + p4[2]]) {
        ctx.globalAlpha = Math.min(1, 1.6 * decke) * alpha;
        ctx.drawImage(at.im, p4[0], p4[1], p4[2], p4[3], Math.round(x), y, p4[2], p4[3]);
        ctx.globalAlpha = Math.min(1, 1.6 * decke) * alpha * 0.6;
        ctx.drawImage(sh, Math.round(x), y);
      }
    }
    ctx.globalAlpha = 1;
  };
  clouds(skyFrom, 1 - blend);
  if (blend > 0) clouds(skyTo, blend);
  // Runde 8: über der Decke ein dunkler Verlauf von oben bis zum Horizont
  const decke = W.decke ?? 0;
  if (decke > 0.002) {
    const g = ctx.createLinearGradient(0, 0, 0, hor);
    g.addColorStop(0, `rgba(14, 16, 24, ${Math.min(1, 1.15 * decke)})`);
    g.addColorStop(0.55, `rgba(14, 16, 24, ${0.45 * decke})`);
    g.addColorStop(1, 'rgba(14, 16, 24, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, d.breite, hor);
  }
  // Von links über dem Horizont ein dunkler Verlauf (dort kommt das Unwetter her)
  if (W.sturm > 0) {
    const g = ctx.createLinearGradient(0, 0, d.breite, 0);
    g.addColorStop(0, `rgba(22, 24, 40, ${0.3 * W.sturm})`);
    g.addColorStop(1, 'rgba(22, 24, 40, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, d.breite, hor);
  }

  // Ebenen bis zum Wasser
  const drawLayer = (o: Ort, at: NonNullable<ReturnType<typeof atlasOf>>, id: string) => {
    const im = at.im!;
    const part = at.a.teile[`${o.id}_${id}`];
    const rows = d.zeilen[`${o.id}_${id}`] ?? [0, d.hoehe];
    if (!part || rows[1] <= rows[0]) return;
    const clean = spiel.sauber[`${o.id}_${id}`] ?? [0, d.breite];
    // Wasser: das ganze Bild im Tempo des Ufers, sonst zerfällt die Spiegelung (Hinweis „Wasser in der Fahrt“)
    const v = id === 'wasser' ? -(s.w - (wege[orte.indexOf(o)]?.mitte ?? 0)) * tempo('ufer') : versatzOf(o, id);
    layer(ctx, d, im, part, v, rows, clean);
    const ps = at.a.teile[`${o.id}_${id}_sturm`];
    const a = clamp(W.sturm * sturmTempo(id), 0, 1);
    if (ps && a > 0) {
      ctx.globalAlpha = a;
      layer(ctx, d, im, ps, v, rows, clean);
      ctx.globalAlpha = 1;
    }
  };
  for (const id of ['ferne', 'mitte', 'ufer']) both((o, at) => drawLayer(o, at, id));
  // Nebel des Unwetters über der Ferne
  if (W.nebel > 0) veil(ctx, d, '#9ea4ab', W.nebel, hor);
  both((o, at) => drawLayer(o, at, 'wasser'));
  // Runde 8: Seegang ab Nah, dann Dunst am Horizont, je Ort in seinem Teil des Bilds
  const sg = spiel.seegang;
  const see = W.see ?? 0;
  // Weg des Wassers: unten im Tempo der Ebene vorn nach links; liegt das Boot, fließt der Fluss vorbei
  const weg = s.ripple - s.w * tempo('vorn');
  both((o) => {
    if (sg && see > 0) drawSea(ctx, { sg, d, o, see, wind: W.wind, weg, now: s.now });
    drawHaze(ctx, d, o, W.dunst ?? 0, W.wind, s.now);
  });
  both((o, at) => drawLayer(o, at, 'dinge'));
  ripples(ctx, d, s, hor, tempo('ufer'), tempo('vorn'));

  // Licht der Tageszeit auf Boot und Anlegestellen
  const boatTone = mixHex(skyFrom.bootTon, skyTo.bootTon, blend);
  const lights = skyFrom.lichter + (skyTo.lichter - skyFrom.lichter) * blend;
  const boat = boote.boote[s.boat] ?? Object.values(boote.boote)[0]!;
  const xBug = boat.fahrt[0] + boat.bug;
  /** Laternen: [x, y, Streuwert] */
  const glows: [number, number, number][] = [];

  // Anlegestellen (Halt 1 bis n − 1) und der Durchlass (Halt n), hinten nach der Ebene dinge
  const dock = spiel.anlegestelle[s.biome];
  const gate = spiel.durchlass[s.biome];
  const docks: number[] = [];
  if (dock)
    for (let j = 1; j < s.n; j++) {
      const x = Math.round(xBug + (j * L - s.w)) - dock.anker[0];
      if (x < d.breite && x + dock.groesse[0] > 0) docks.push(x);
    }
  const sturmDock = clamp(W.sturm * (dock?.sturmTempo ?? 0.85), 0, 1);
  const drawDock = (part: 'hinten' | 'vorn') => {
    if (!dock) return;
    const im = img(`fahrt/${dock[part]}`);
    const st = img(`fahrt/${part === 'hinten' ? dock.hintenSturm : dock.vornSturm}`);
    if (!im) return;
    for (const x of docks) {
      ctx.drawImage(tone(im, boatTone), x, dock.y);
      if (st && sturmDock > 0) {
        ctx.globalAlpha = sturmDock;
        ctx.drawImage(tone(st, boatTone), x, dock.y);
        ctx.globalAlpha = 1;
      }
    }
  };
  drawDock('hinten');
  for (const x of docks) for (const [lx, ly] of dock!.lampen) glows.push([x + lx, dock!.y + ly, x]);
  const gateX = gate ? Math.round(xBug + (s.n * L - s.w)) - gate.anker[0] : 0;
  const gateOn = !!gate && gateX < d.breite && gateX + gate.hinten.groesse[0] > 0;
  const drawGate = (part: 'hinten' | 'vorn') => {
    if (!gate || !gateOn) return;
    const g = gate[part];
    const im = img(`fahrt/${g.datei}`);
    const st = img(`fahrt/${g.sturm}`);
    if (!im) return;
    ctx.drawImage(im, gateX, g.y);
    const a = clamp(W.sturm * gate.sturmTempo, 0, 1);
    if (st && a > 0) {
      ctx.globalAlpha = a;
      ctx.drawImage(st, gateX, g.y);
      ctx.globalAlpha = 1;
    }
  };
  drawGate('hinten');
  if (gateOn) for (const [lx, ly] of gate!.lampen) glows.push([gateX + lx, ly, gateX]);

  // Morgendunst in Strecke 1 und der Schleier über dem klareren Ort
  const md = spiel.spiel.morgendunst;
  if (md && s.w < L) veil(ctx, d, md.farbe, md.deckkraft * (1 - smooth(tau / ortMitte(T, 0))), hor);
  const truebe = skyFrom.wetter.truebe + (skyTo.wetter.truebe - skyFrom.wetter.truebe) * blend;
  const murky = skyFrom.wetter.truebe > skyTo.wetter.truebe ? skyFrom : skyTo;
  both((o, _at, clip) => veil(ctx, d, murky.himmel, (truebe - o.wetter.truebe) * 0.75, hor, clip));

  // Dunkle Decke des Unwetters über allem bis zum Boot
  const dark = '#141626';
  if (W.dunkel > 0) {
    ctx.fillStyle = rgba(dark, 0.6 * W.dunkel);
    ctx.fillRect(0, 0, d.breite, d.hoehe);
  }
  // Runde 8: grauer Schleier über allem bis zum Boot (fern)
  veil(ctx, d, '#545a66', 0.85 * (W.fern ?? 0), hor + 6, undefined, 0.9);
  // Laternen der Anlegestellen und des Durchlasses
  const strength = 0.5 * lights + 0.1;
  for (const [lx, ly, seed] of glows) lamp(ctx, lx, ly, strength, s.now, d.wl, seed);

  // Das Boot (Wasserlinie auf 306, waagrecht mittig) mit der Truppe. Runde 8: Es schaukelt mit den Wellen (rock.ts);
  // Tiere, Laternen, Rauch, Spiegelung und Treffer der Maus rücken mit dem Punkt des Boots, auf dem sie sind
  const bx = boat.fahrt[0];
  const by = boat.fahrt[1];
  const rock: Rock | undefined = s.sway !== undefined && W.schaukeln ? rockOf(swayAt(s.sway, W.schaukeln), boat, spiel.schaukeln?.grenzen[s.boat]) : undefined;
  const own: Hit[] = [];
  const layers = drawBoat(scratchCtx(boat.w + 2 * PAD, boat.h + 2 * PAD), { boat, view: s.view, crew: s.crew, x: PAD, y: PAD, tone: boatTone, now: s.now, chat: s.chat, look: s.look, rock }, own);
  if (layers) {
    // Die Spiegelung geht gegengleich: hebt sich das Boot, sinkt das Spiegelbild
    ctx.save();
    ctx.globalAlpha = 0.28;
    ctx.translate(bx - PAD, d.wl + boat.wl + 1 + PAD);
    ctx.scale(1, -1);
    ctx.drawImage(layers.outside, 0, 0);
    ctx.restore();
    ctx.drawImage(layers.view, bx - PAD, by - PAD);
    // drawBoat meldet die Tiere in Pixeln der Hilfsleinwand, auf der das Boot bei (PAD, PAD) liegt
    for (const h of own) hits.push({ ...h, x: h.x + bx - PAD, y: h.y + by - PAD });
    shadeBoat(ctx, layers.view, bx - PAD, by - PAD, dark, Math.min(0.6, 0.9 * W.dunkel));
  }
  const onBoat = (x: number, y: number): [number, number] => {
    const [ox, oy] = rockAt(rock, x, y);
    return [bx + x + ox, by + y + oy];
  };
  for (const [lx, ly] of boat.lampen) {
    const [X, Y] = onBoat(lx, ly);
    lamp(ctx, X, Y, strength, s.now, d.wl, lx);
    glows.push([X, Y, lx]);
  }
  if (boat.lichterkette)
    for (const [i, [lx, ly]] of boat.lichterkette.entries()) {
      const [X, Y] = onBoat(lx, ly);
      ctx.fillStyle = `rgba(255, 220, 150, ${(0.25 + 0.5 * lights) * (0.75 + 0.25 * Math.sin(s.now * 2 + i))})`;
      ctx.fillRect(Math.round(X - 1), Math.round(Y - 1), 2, 2);
    }
  smoke(ctx, (boat.rauch_alle ?? (boat.rauch ? [boat.rauch] : [])).map(([x, y]) => onBoat(x, y)), 0, 0, s.now, W.wind, W.sturm);
  // Runde 8: Wasserkante am Rumpf, wo er tiefer liegt als ohne Schaukeln oder eine Welle an ihm hochläuft
  const hullImg = img(`boot/${boat.dateien[s.view]}`);
  if (rock && hullImg) {
    const water = waterColor(d, A, true) ?? hexRgb('#3a4a52');
    const calmWater = waterColor(d, A, false) ?? water;
    const k = clamp(W.sturm * sturmTempo('wasser'), 0, 1);
    const wc = calmWater.map((c, i) => c + (water[i]! - c) * k) as [number, number, number];
    hullEdge(ctx, hullImg, rock, bx, by, wc, see, (x, y) => seaHeight(A, x, y));
  }

  // Anlegestellen vorn, dann die Ebene vorn, dann der Durchlass vorn
  drawDock('vorn');
  if (dock && W.dunkel > 0) {
    const im = img(`fahrt/${dock.vorn}`);
    if (im) for (const x of docks) shadeBoat(ctx, tone(im, boatTone), x, dock.y, dark, Math.min(0.6, 0.9 * W.dunkel));
  }
  both((o, at) => {
    const part = at.a.teile[`${o.id}_vorn`];
    if (!at.im || !part) return;
    const rows = d.zeilen[`${o.id}_vorn`] ?? [0, d.hoehe];
    const clean = spiel.sauber[`${o.id}_vorn`] ?? [0, d.breite];
    layer(ctx, d, at.im, part, versatzOf(o, 'vorn'), rows, clean);
    const ps = at.a.teile[`${o.id}_vorn_sturm`];
    const a = clamp(W.sturm * sturmTempo('vorn'), 0, 1);
    if (ps && a > 0) {
      ctx.globalAlpha = a;
      layer(ctx, d, at.im, ps, versatzOf(o, 'vorn'), rows, clean);
      ctx.globalAlpha = 1;
    }
  });
  drawGate('vorn');
  // Die Kulisse, hinter der der Ort wechselt
  if (xk !== null) {
    const kul = img(`fahrt/${bio.kulisse.datei}`);
    if (kul) ctx.drawImage(kul, Math.round(xk - bio.kulisse.mitte), 0);
  }
  // Unterhalb des Horizonts noch etwas dunkler
  if (W.dunkel > 0) {
    ctx.fillStyle = rgba(dark, 0.3 * W.dunkel);
    ctx.fillRect(0, hor, d.breite, d.hoehe - hor);
  }
  // Regen (nicht in den Räumen des Boots), Blätter im Wind, Wetterleuchten
  const regen = Math.max(A.wetter.regen + (B.wetter.regen - A.wetter.regen) * w, W.regen);
  if (regen > 0) drawRain(ctx, d, regen, s.now, W.wind, W.schraeg ?? 0, s.view === 'quer' ? insideMask(boat.dateien.innen, boat.w, boat.h, rock) : null, bx - PAD, by - PAD);
  if (W.blaetter > 0) drawLeaves(ctx, d, W.blaetter, s.now, W.wind, LEAF[s.biome] ?? LEAF.tannenwald!);
  if (s.flash !== undefined) {
    if (s.flash > 0) {
      ctx.fillStyle = `rgba(235, 240, 255, ${0.3 * s.flash})`;
      ctx.fillRect(0, 0, d.breite, d.hoehe);
    }
  } else if (W.blitz > 0) {
    const every = W.blitz / 1000;
    const ph = (s.now % every) / every;
    if (ph < 0.025) {
      ctx.fillStyle = `rgba(235, 240, 255, ${0.25 * (1 - ph / 0.025)})`;
      ctx.fillRect(0, 0, d.breite, d.hoehe);
    }
  }
  // Nacht zwischen zwei Tagen; die Lichter verlöschen zuletzt
  if (s.night > 0) {
    const n = spiel.spiel.nacht;
    ctx.fillStyle = rgba(n.farbe, clamp(s.night, 0, 1) * n.deckkraft);
    ctx.fillRect(0, 0, d.breite, d.hoehe);
    if (s.night < 1) {
      ctx.globalAlpha = (1 - s.night) * 0.8;
      for (const [lx, ly, seed] of glows) lamp(ctx, lx, ly, strength, s.now, d.wl, seed);
      ctx.globalAlpha = 1;
    }
  }
  return true;
}

/**
 * Runde 8, Hinweis „Schaukeln, Wasserkante am Rumpf“: je Spalte des Boots die Zeilen [r − welle, r + dy) in der Farbe
 * des Wassers, wo welle > 0 die oberste heller; r ist die erste Zeile Wasser unter dem Rumpf ohne Schaukeln
 */
function hullEdge(ctx: CanvasRenderingContext2D, hull: HTMLImageElement, rock: Rock, bx: number, by: number, w: [number, number, number], see: number, waveAt: (x: number, y: number) => number): void {
  const rows = hullBottom(hull);
  const deep = w.map((c, i) => Math.round(c + ([10, 14, 20][i]! - c) * 0.25));
  const light = w.map((c, i) => Math.round(c + ([226, 233, 236][i]! - c) * 0.5));
  const fill = `rgba(${deep[0]}, ${deep[1]}, ${deep[2]}, 0.88)`;
  const top = `rgba(${light[0]}, ${light[1]}, ${light[2]}, 0.6)`;
  for (let x = 0; x < rows.length; x++) {
    const r = rows[x]!;
    if (r < 0) continue;
    const X = bx + x + rock.dx(r - 1);
    const dy = rock.dy(x);
    const welle = see > 0 ? Math.max(0, Math.round(waveAt(X, by + r + 1) * (1 + 2.6 * see) - 0.2)) : 0;
    const y0 = by + r - welle;
    const y1 = by + r + dy;
    if (y1 <= y0) continue;
    ctx.fillStyle = fill;
    ctx.fillRect(X, y0, 1, y1 - y0);
    if (welle > 0) {
      ctx.fillStyle = top;
      ctx.fillRect(X, y0, 1, 1);
    }
  }
}

/** Die Maske der Innenräume, mit dem Boot geschaukelt (Regen fällt nicht hinein), mit dem Rand PAD */
let maskCanvas: HTMLCanvasElement | null = null;
function insideMask(file: string, w: number, h: number, rock: Rock | undefined): HTMLCanvasElement | null {
  const im = img(`boot/${file}`);
  if (!im) return null;
  maskCanvas ??= document.createElement('canvas');
  if (maskCanvas.width !== w + 2 * PAD || maskCanvas.height !== h + 2 * PAD) {
    maskCanvas.width = w + 2 * PAD;
    maskCanvas.height = h + 2 * PAD;
  }
  const g = maskCanvas.getContext('2d')!;
  g.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
  drawRocked(g, im, w, h, PAD, PAD, rock);
  return maskCanvas;
}

let scratchCanvas: HTMLCanvasElement | null = null;
function scratchCtx(w: number, h: number): CanvasRenderingContext2D {
  scratchCanvas ??= document.createElement('canvas');
  if (scratchCanvas.width !== w || scratchCanvas.height !== h) {
    scratchCanvas.width = w;
    scratchCanvas.height = h;
  }
  const g = scratchCanvas.getContext('2d')!;
  g.clearRect(0, 0, w, h);
  return g;
}

/** Die Tiere als Treffer für die Maus: drawBoat hat sie in Pixeln des Boots gemeldet */
/** Dunkle Fassung einer Wolkenbank, zwischengespeichert */
const shades = new Map<string, HTMLCanvasElement>();
function bankShade(im: HTMLImageElement, p: [number, number, number, number]): HTMLCanvasElement {
  const key = `${im.src}|${p.join(',')}`;
  let c = shades.get(key);
  if (c) return c;
  c = document.createElement('canvas');
  c.width = p[2];
  c.height = p[3];
  const g = c.getContext('2d')!;
  g.drawImage(im, p[0], p[1], p[2], p[3], 0, 0, p[2], p[3]);
  g.globalCompositeOperation = 'source-in';
  g.fillStyle = '#20222e';
  g.fillRect(0, 0, p[2], p[3]);
  shades.set(key, c);
  return c;
}

/** Kräusel auf dem Wasser: ziehen mit dem Weg, oben im Tempo des Ufers, unten im Tempo vorn; liegt das Boot, fließt der Fluss vorbei */
function ripples(ctx: CanvasRenderingContext2D, d: Durchfahrt, s: SceneState, hor: number, tUfer: number, tVorn: number): void {
  const top = Math.max(hor + 4, d.wl - 50);
  for (let i = 0; i < 26; i++) {
    const r = ((i * 0.618034) % 1) * 0.999;
    const y = Math.round(top + (d.hoehe - top) * (i / 26) ** 1.4);
    const k = (y - top) / Math.max(1, d.hoehe - top);
    const t = tUfer + (tVorn - tUfer) * k;
    const len = 3 + Math.round(k * 6);
    const span = d.breite + 40;
    const x = ((((r * span - (s.w * t) + s.ripple * (0.6 + k)) % span) + span) % span) - 20;
    ctx.fillStyle = `rgba(230, 236, 240, ${0.07 + 0.08 * k})`;
    ctx.fillRect(Math.round(x), y, len, 1);
  }
}

/** Rauch aus Schornstein und Ofenrohr: bei Ruhe fast gerade nach oben, leicht nach hinten; im Sturm flach nach vorn */
function smoke(ctx: CanvasRenderingContext2D, from: [number, number][], bx: number, by: number, now: number, wind: number, sturm: number): void {
  const drift = -6 + 34 * clamp((wind - 0.2) / 0.32, 0, 1);
  const rise = 24 - 14 * sturm;
  for (const [sx, sy] of from)
    for (let i = 0; i < 6; i++) {
      const age = (now * 0.5 + i / 6 + sx * 0.01) % 1;
      ctx.fillStyle = `rgba(205, 205, 205, ${0.32 * (1 - age)})`;
      ctx.beginPath();
      ctx.arc(bx + sx + age * drift, by + sy - age * rise, 1.4 + age * 3, 0, Math.PI * 2);
      ctx.fill();
    }
}

/** Ein fester Streuwert zwischen 0 und 1 je Zahl und Kanal */
const hash01 = (i: number, k: number) => (Math.imul(Math.imul(i + 1, 374761393) ^ Math.imul(k, 668265263), 1274126177) >>> 0) / 4294967296;

const rainCanvas = { c: null as HTMLCanvasElement | null };
/**
 * Regen: fällt mit dem Wind nach rechts, (40 + 160 · wind) Pixel je Sekunde, und 300 nach unten, jeder Tropfen 0,85- bis
 * 1,15-mal so schnell; schraeg Pixel längere Striche (Runde 8). In den Räumen des Boots regnet es nicht (Maske bei mx, my)
 */
function drawRain(ctx: CanvasRenderingContext2D, d: Durchfahrt, n: number, t: number, wind: number, schraeg: number, mask: HTMLCanvasElement | null, mx: number, my: number): void {
  rainCanvas.c ??= document.createElement('canvas');
  const c = rainCanvas.c;
  if (c.width !== d.breite || c.height !== d.hoehe) {
    c.width = d.breite;
    c.height = d.hoehe;
  }
  const g = c.getContext('2d')!;
  g.clearRect(0, 0, d.breite, d.hoehe);
  g.strokeStyle = 'rgba(205, 220, 238, 0.35)';
  g.lineWidth = 1;
  g.beginPath();
  const len = 7 + schraeg;
  const H = d.hoehe + len + 6;
  const span = d.breite + 120;
  const slant = (40 + 160 * wind) / 300;
  const norm = Math.hypot(slant, 1);
  const lx = (len * slant) / norm;
  const ly = len / norm;
  for (let i = 0; i < Math.round(n); i++) {
    // Je Tropfen eigene Streuwerte; mit i · 0,618 und i · 0,382 lägen die Tropfen am Anfang auf einer schrägen Linie
    const f = 0.85 + hash01(i, 1) * 0.3;
    // Weg des Tropfens, senkrecht gemessen; waagrecht wandert er mit slant
    const path = t * 300 * f + hash01(i, 2) * H;
    const y = (path % H) - len;
    const x = ((hash01(i, 3) * span + path * slant) % span) - 60;
    g.moveTo(Math.round(x - lx), Math.round(y - ly));
    g.lineTo(Math.round(x), Math.round(y));
  }
  g.stroke();
  if (mask) {
    g.globalCompositeOperation = 'destination-out';
    g.drawImage(mask, Math.round(mx), Math.round(my));
    g.globalCompositeOperation = 'source-over';
  }
  ctx.drawImage(c, 0, 0);
}

function drawLeaves(ctx: CanvasRenderingContext2D, d: Durchfahrt, n: number, t: number, wind: number, colors: string[]): void {
  for (let i = 0; i < Math.round(n); i++) {
    const speed = 40 + wind * 160 + ((i * 0.7548) % 1) * 40;
    const span = d.breite + 40;
    const x = ((((i * 0.618034) % 1) * span + t * speed) % span) - 20;
    const y = (((i * 0.381966) % 1) * d.hoehe * 0.8 + Math.sin(t * 1.7 + i) * 10 + t * 12) % d.hoehe;
    ctx.fillStyle = colors[i % colors.length]!;
    const flip = Math.sin(t * 6 + i) > 0;
    ctx.fillRect(Math.round(x), Math.round(y), flip ? 2 : 1, flip ? 1 : 2);
  }
}

/**
 * F46 (Freundeskarte): ein Stück Landschaft aus der Fahrt für das Bild auf der Karte: Himmel, Ferne, Mitte und Ufer des
 * ersten Orts im Biom, ab (sx, sy) in Bildpixeln. false, solange die Bilder noch laden
 */
export function drawBackdrop(ctx: CanvasRenderingContext2D, biome: string, sx: number, sy: number, w: number, h: number): boolean {
  const d = loadDurchfahrt();
  if (!d) return false;
  const bio = d.biome[biome] ?? Object.values(d.biome)[0]!;
  const o = bio.orte[0]!;
  const at = d.atlas[o.id];
  const im = at ? img(`fahrt/${at.datei}`) : null;
  if (!at || !im) return false;
  for (const id of ['himmel', 'ferne', 'mitte', 'ufer', 'wasser']) {
    const p = at.teile[`${o.id}_${id}`];
    if (!p) continue;
    const off = id === 'himmel' ? 0 : d.rand;
    ctx.drawImage(im, p[0] + off + sx, p[1] + sy, w, h, 0, 0, w, h);
  }
  return true;
}

export type { FahrtSpiel };
